import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  Invoice, 
  InvoiceCreateDto, 
  InvoiceUpdateDto,
  InvoiceResponse
} from "../../modules/shared/model";
import { 
  getInvoices as getInvoicesAction,
  getInvoiceById as getInvoiceByIdAction,
  createInvoiceAction,
  updateInvoiceAction,
  deleteInvoiceAction,
  bulkCreateInvoicesAction,
  bulkUpdateInvoicesAction
} from "../Actions/InvoiceActions";

// Async Thunks
export const fetchInvoices = createAsyncThunk("invoices/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getInvoicesAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch invoices"); 
  }
});

export const fetchInvoiceById = createAsyncThunk("invoices/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getInvoiceByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch invoice"); 
  }
});

export const createInvoice = createAsyncThunk("invoices/create", async (data: InvoiceCreateDto, { rejectWithValue }) => {
  try { 
    return await createInvoiceAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create invoice"); 
  }
});

export const updateInvoice = createAsyncThunk("invoices/update", async ({ id, data }: { id: string; data: InvoiceUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateInvoiceAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update invoice"); 
  }
});

export const deleteInvoice = createAsyncThunk("invoices/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteInvoiceAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete invoice"); 
  }
});

export const bulkCreateInvoices = createAsyncThunk("invoices/bulkCreate", async (invoices: InvoiceCreateDto[], { rejectWithValue }) => {
  try { 
    return await bulkCreateInvoicesAction(invoices); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk create invoices"); 
  }
});

export const bulkUpdateInvoices = createAsyncThunk("invoices/bulkUpdate", async (invoices: Array<{ id: string; data: InvoiceUpdateDto }>, { rejectWithValue }) => {
  try { 
    return await bulkUpdateInvoicesAction(invoices); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk update invoices"); 
  }
});

// State interface
interface InvoiceState {
  // All invoices (paginated)
  invoices: Invoice[];
  currentInvoice: Invoice | null;
  invoicesLoading: boolean;
  invoicesError: string | null;
  invoicesTotalCount?: number;
  invoicesCurrentPage: number;
  invoicesHasNextPage: boolean;
  invoicesHasPreviousPage: boolean;
  invoicesSearchQuery: string;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: InvoiceState = {
  // All invoices
  invoices: [],
  currentInvoice: null,
  invoicesLoading: false,
  invoicesError: null,
  invoicesTotalCount: 0,
  invoicesCurrentPage: 1,
  invoicesHasNextPage: false,
  invoicesHasPreviousPage: false,
  invoicesSearchQuery: "",
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const invoiceSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearInvoicesError(state) { 
      state.invoicesError = null; 
    },
    clearCurrentInvoice(state) { 
      state.currentInvoice = null; 
    },
    setInvoicesSearchQuery(state, action: PayloadAction<string>) { 
      state.invoicesSearchQuery = action.payload; 
    },
    setInvoiceFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllInvoices(state) {
      state.invoices = [];
      state.currentInvoice = null;
      state.invoicesTotalCount = 0;
    },
    updateInvoiceFromForm(state, action: PayloadAction<Partial<Invoice>>) {
      if (state.currentInvoice) {
        state.currentInvoice = { ...state.currentInvoice, ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    // All Invoices Extra Reducers
    builder
      .addCase(fetchInvoices.pending, (state) => { 
        state.invoicesLoading = true; 
        state.invoicesError = null; 
      })
      .addCase(fetchInvoices.fulfilled, (state, action: PayloadAction<InvoiceResponse>) => { 
        state.invoicesLoading = false; 
        state.invoices = action.payload.data || []; 
        state.invoicesTotalCount = action.payload.totalCount || 0; 
        state.invoicesCurrentPage = action.payload.page || 1;
        state.invoicesHasNextPage = action.payload.hasNextPage || false;
        state.invoicesHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchInvoices.rejected, (state, action: any) => { 
        state.invoicesLoading = false; 
        state.invoicesError = action.payload as string; 
      });

    builder
      .addCase(fetchInvoiceById.pending, (state) => { 
        state.invoicesLoading = true; 
        state.invoicesError = null; 
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action: PayloadAction<Invoice>) => { 
        state.invoicesLoading = false; 
        state.currentInvoice = action.payload; 
      })
      .addCase(fetchInvoiceById.rejected, (state, action: any) => { 
        state.invoicesLoading = false; 
        state.invoicesError = action.payload as string; 
      });

    // Create Extra Reducers
    builder
      .addCase(createInvoice.pending, (state) => { 
        state.invoicesLoading = true; 
        state.invoicesError = null; 
      })
      .addCase(createInvoice.fulfilled, (state, action: PayloadAction<Invoice>) => { 
        state.invoicesLoading = false; 
        state.invoices.unshift(action.payload); 
        state.invoicesTotalCount = (state.invoicesTotalCount || 0) + 1; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createInvoice.rejected, (state, action: any) => { 
        state.invoicesLoading = false; 
        state.invoicesError = action.payload as string; 
      });

    // Update Extra Reducers
    builder
      .addCase(updateInvoice.pending, (state) => { 
        state.invoicesLoading = true; 
        state.invoicesError = null; 
      })
      .addCase(updateInvoice.fulfilled, (state, action: PayloadAction<Invoice>) => {
        state.invoicesLoading = false;
        const index = state.invoices.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.invoices[index] = action.payload;
        if (state.currentInvoice?.id === action.payload.id) state.currentInvoice = action.payload;
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateInvoice.rejected, (state, action: any) => { 
        state.invoicesLoading = false; 
        state.invoicesError = action.payload as string; 
      });

    // Delete Extra Reducers
    builder
      .addCase(deleteInvoice.pending, (state) => { 
        state.invoicesLoading = true; 
        state.invoicesError = null; 
      })
      .addCase(deleteInvoice.fulfilled, (state, action: PayloadAction<string>) => { 
        state.invoicesLoading = false; 
        state.invoices = state.invoices.filter(x => x.id !== action.payload); 
        if (state.currentInvoice?.id === action.payload) state.currentInvoice = null; 
        state.invoicesTotalCount = Math.max(0, (state.invoicesTotalCount || 0) - 1); 
      })
      .addCase(deleteInvoice.rejected, (state, action: any) => { 
        state.invoicesLoading = false; 
        state.invoicesError = action.payload as string; 
      });

    // Bulk Create Extra Reducers
    builder
      .addCase(bulkCreateInvoices.pending, (state) => { 
        state.invoicesLoading = true; 
        state.invoicesError = null; 
      })
      .addCase(bulkCreateInvoices.fulfilled, (state, action: PayloadAction<Invoice[]>) => { 
        state.invoicesLoading = false; 
        state.invoices.unshift(...action.payload); 
        state.invoicesTotalCount = (state.invoicesTotalCount || 0) + action.payload.length; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkCreateInvoices.rejected, (state, action: any) => { 
        state.invoicesLoading = false; 
        state.invoicesError = action.payload as string; 
      });

    // Bulk Update Extra Reducers
    builder
      .addCase(bulkUpdateInvoices.pending, (state) => { 
        state.invoicesLoading = true; 
        state.invoicesError = null; 
      })
      .addCase(bulkUpdateInvoices.fulfilled, (state, action: PayloadAction<Invoice[]>) => {
        state.invoicesLoading = false;
        action.payload.forEach(updatedInvoice => {
          const index = state.invoices.findIndex(x => x.id === updatedInvoice.id);
          if (index !== -1) state.invoices[index] = updatedInvoice;
          if (state.currentInvoice?.id === updatedInvoice.id) state.currentInvoice = updatedInvoice;
        });
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkUpdateInvoices.rejected, (state, action: any) => { 
        state.invoicesLoading = false; 
        state.invoicesError = action.payload as string; 
      });
  }
});

export const { 
  clearInvoicesError,
  clearCurrentInvoice, 
  setInvoicesSearchQuery,
  setInvoiceFormDirty,
  clearAllInvoices,
  updateInvoiceFromForm
} = invoiceSlice.actions;

export default invoiceSlice.reducer;
